
import { useState, useEffect } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { debugLogger } from "@/utils/debugLogger";

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    debugLogger.log('AUTH', 'AuthProvider useEffect running');
    
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        debugLogger.log('AUTH', 'Auth state change detected', { 
          event, 
          hasSession: !!currentSession,
          userId: currentSession?.user?.id,
          currentPath: location.pathname
        });
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (event === 'SIGNED_IN') {
          // FIXED: Only navigate to CMS if not already on a CMS route
          const isOnCMSRoute = location.pathname.startsWith('/cms');
          const isOnAuthRoute = location.pathname.startsWith('/auth');
          
          if (!isOnCMSRoute && !isOnAuthRoute) {
            debugLogger.log('AUTH', 'User signed in, navigating to /cms from non-CMS route');
            setTimeout(() => {
              navigate('/cms');
            }, 0);
          } else {
            debugLogger.log('AUTH', 'User signed in but already on CMS/auth route - no navigation needed');
          }
        } else if (event === 'SIGNED_OUT') {
          debugLogger.log('AUTH', 'User signed out, navigating to /auth/login');
          setTimeout(() => {
            navigate('/auth/login');
          }, 0);
        } else if (event === 'TOKEN_REFRESHED') {
          debugLogger.log('AUTH', 'Token refreshed - no navigation needed');
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      debugLogger.log('AUTH', 'Initial session check', { 
        hasSession: !!currentSession,
        userId: currentSession?.user?.id 
      });
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    });

    return () => {
      debugLogger.log('AUTH', 'AuthProvider cleanup');
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname]);

  return {
    user,
    session,
    loading,
    setLoading
  };
};
